import * as z from "zod";
import type { Tables } from "./database.types.ts";

export const ProblemJsonSchema = z
  .object({
    type: z.url().nullable().optional(),
    title: z.string(),
    status: z.number(),
    detail: z.string().nullable().optional(),
    instance: z.string().url().nullable().optional(),
  })
  .loose();

export type ProblemJson = z.infer<typeof ProblemJsonSchema>;
export type MessageOrProblem = ProblemJson | { message: string };

export const EmailRequestSchema = z.object({
  email: z.string(),
  message: z.string(),
  subject: z.string(),
});

export type EmailRequest = z.infer<typeof EmailRequestSchema>;

export const TextRequestSchema = z.object({
  message: z.string(),
  phoneNumber: z
    .string()
    .regex(/^\+[1-9]\d{10,14}$/, "Phone number must be in international format (+1234567890)"),
});

export type TextRequest = z.infer<typeof TextRequestSchema>;

export const PaypalCaptureSuccessResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type PaypalCaptureSuccessResponse = z.infer<typeof PaypalCaptureSuccessResponseSchema>;

export const PaypalCaptureResponseSchema = z.union([
  PaypalCaptureSuccessResponseSchema,
  ProblemJsonSchema,
]);
export type PaypalCaptureResponse = z.infer<typeof PaypalCaptureResponseSchema>;

export type InvoiceRequestData = {
  email: string;
  name: string;
  phone?: string;
  transaction_id: number;
  amount: number;
  description?: string;
  user_id: string;
};

export type StripeCustomerData = {
  email: string;
  name: string;
  phone?: string;
};

export type StripeInvoiceData = {
  customer_id: string;
  amount: number;
  description?: string;
  metadata?: Record<string, string | number>;
};

export type NotificationRule = {
  rule_id: number;
  message: string;
  subject?: string;
  method: "email" | "text" | "both";
  hours_before_event?: number;
  hours_from_event?: number;
  is_active: boolean; // Added is_active
};

export type AnalyticData = {
  class: "LOG" | "ERROR"; // Changed type to class
  event: {
    event: string;
    transaction_id?: number;
    event_id?: number;
    message?: string;
    updated_data?: Record<string, any>;
    errorMessage?: string;
    recipient_email?: string;
    recipient_phone?: string;
    subject?: string;
  };
};

export type LogEntry = {
  date: string;
  user_id: string;
  message: string;
  data?: string;
};

export type PaymentData = {
  processor_transaction_id?: string;
  processor_fee?: number;
  captured_at?: string;
  status?: string;
};

export const PaypalCaptureRequestSchema = z.object({
  orderId: z.string(),
  transactionIds: z.array(z.number()),
  userId: z.string(),
});

export type PaypalCaptureRequest = z.infer<typeof PaypalCaptureRequestSchema>;

export const PaypalOrderCreateRequestSchema = z.object({
  transactionIds: z.array(z.number()),
});

export type PaypalOrderCreateRequest = z.infer<typeof PaypalOrderCreateRequestSchema>;

// Database types
export type Event = Tables<"events">;
export type Session = Tables<"sessions">;
export type User = Tables<"users">;
export type Class = Tables<"classes">;
export type Transaction = Tables<"transactions">;

// 1. Peer Tutoring Booking
export const EventInsertSchema = z.object({
  title: z.string(),
  description: z.string().nullable().optional(),
  start_date: z.string(), // ISO string
  end_date: z.string(), // ISO string
  service_type_id: z.number(),
  location: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  public_notes: z.string().nullable().optional(),
  private_notes: z.string().nullable().optional(),
});

export const CreateOneTimeBookingRequestSchema = z.object({
  eventData: EventInsertSchema,
  staffId: z.string(),
  studentUserId: z.string(),
  parentUserId: z.string(),
  discountPercentage: z.number().min(0).max(100).default(0),
});

export type CreateOneTimeBookingResponse = {
  event: Event;
  chargeTransaction: Transaction;
  paymentTransaction: Transaction;
};

export type CreateOneTimeBookingRequest = z.infer<typeof CreateOneTimeBookingRequestSchema>;

// 2. Class Signup
export const UserSchema = z.object({
  user_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
});

export const ClassSchema = z.object({
  class_id: z.number(),
  title: z.string(),
});

export const GroupClassSignupRequestSchema = z.object({
  client: UserSchema,
  student: UserSchema,
  classes: z.array(ClassSchema),
  selectedSessions: z.array(z.number()),
  chargedAmount: z.number(),
});

export type GroupClassSignupRequest = z.infer<typeof GroupClassSignupRequestSchema>;

// 3. Admin Class/Session Creation
export const SessionInsertSchema = z.object({
  title: z.string(),
  description: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  public_notes: z.string().nullable().optional(),
  resources: z.string().nullable().optional(),
  curriculum_notes: z.string().nullable().optional(),
  staff_id: z.string().nullable().optional(),
  start_date: z.string(), // ISO string
  end_date: z.string(), // ISO string
  url: z.string().nullable().optional(),
});

export const CreateClassSessionRequestSchema = z.object({
  startDate: z.iso.datetime(),
  endDate: z.iso.datetime(),
  url: z.url(),
  sessionData: SessionInsertSchema,
  classId: z.number(),
  serviceTypeId: z.number(),
});

export type CreateClassSessionRequest = z.infer<typeof CreateClassSessionRequestSchema>;

// 4. Stripe Invoice/Transaction for College Advising Signup
export const CollegeAdvisingSignupRequestSchema = z.object({
  sessionSet: z.string(),
  studentFirstName: z.string(),
  studentLastName: z.string(),
  parentFirstName: z.string(),
  parentLastName: z.string(),
  parentEmail: z.string().email(),
  parentPhone: z.string(),
});

export type CollegeAdvisingSignupRequest = z.infer<typeof CollegeAdvisingSignupRequestSchema>;

// Email validation regex to match your backend
const emailRegex = /^[^\\s@]+@[^\\s@]+\.[^\\s@]+$/;

// Phone validation regex to match your backend
const phoneRegex = /^[+]?[\d\s\-()]{10,}$/;

// Zod schemas for validation
export const CreateInvoiceRequestSchema = z.object({
  email: z.string().min(1, "Email is required").regex(emailRegex, "Invalid email format"),
  name: z
    .string()
    .min(2, "Name is required and must be at least 2 characters")
    .transform((str) => str.trim()),
  phone: z.string().regex(phoneRegex, "Invalid phone format").optional().or(z.literal("")),
  transactionId: z.number(),
  userId: z.string().min(1, "User ID is required"),
  amount: z.number().positive("Amount must be greater than 0"),
  description: z.string().min(1, "Description is required").optional().default("Payment"),
});

export const CreateInvoiceResponseSchema = z.object({
  invoiceId: z.string(),
  customerId: z.string(),
  invoiceUrl: z.string().nullish(),
  amount: z.number(),
  status: z.string(),
  transactionId: z.string(),
});

export const CreateInvoiceResponse = z.object;

export const FinalizeInvoiceRequestSchema = z.object({
  invoiceId: z.string().min(1, "Invoice ID is required"),
  transactionId: z.number(),
  userId: z.string().min(1, "User ID is required"),
});

export const FinalizeInvoiceResponseSchema = z.object({
  invoiceId: z.string(),
  transactionId: z.string(),
  status: z.string(),
  paymentUrl: z.string().optional(),
  message: z.string(),
});

export type CreateInvoiceRequest = z.infer<typeof CreateInvoiceRequestSchema>;
export type CreateInvoiceResponse = z.infer<typeof CreateInvoiceResponseSchema>;
export type FinalizeInvoiceRequest = z.infer<typeof FinalizeInvoiceRequestSchema>;
export type FinalizeInvoiceResponse = z.infer<typeof FinalizeInvoiceResponseSchema>;

export const CalculatePeerTutoringRequestSchema = z.object({
  staffId: z.uuid(),
  serviceTypeId: z.number(),
  startTime: z.iso.datetime(),
  endTime: z.iso.datetime(),
  discountPercentage: z.number().min(0).max(100).default(0),
});

export type CalculatePeerTutoringRequest = z.infer<typeof CalculatePeerTutoringRequestSchema>;

export const CalculatePeerTutoringResponseSchema = z
  .object({
    staff_payment: z.number(),
    user_charge_total: z.number(),
    user_charge_gross: z.number(),
    user_discount: z.number(),
    service_rate: z.number(),
    applied_rate: z.number(),
  })
  .refine((data) => data.user_charge_total >= data.user_discount, {
    message: "Discount cannot be larger than payment",
    path: ["userDiscount"],
  });
export type CalculatePeerTutoringResponse = z.infer<typeof CalculatePeerTutoringResponseSchema>;

// Assuming a structure for the missing PaypalError interface
const PaypalErrorSchema = z.object({
  issue: z.string(),
  description: z.string(),
});

export const CreatePaypalAPIResponseSchema = z.object({
  id: z.string(), // ID for the order
  status: z.enum(["CREATED", "PAYER_ACTION_REQUIRED", "COMPLETED", "VOIDED", "APPROVED", "SAVED"]),
  links: z.array(
    z.object({
      href: z.url(),
      rel: z.enum(["self", "approve", "update", "capture", "payer-action"]),
      method: z.enum(["GET", "POST", "PATCH"]),
    }),
  ),
  error: z.string().optional(),
  debug_id: z.string().optional(),
  details: z.array(PaypalErrorSchema).optional(),
});

// Reusable sub-schemas for the Capture response
const AmountSchema = z.object({
  currency_code: z.string(),
  value: z.string(),
});

const LinkSchema = z.object({
  href: z.string(),
  rel: z.string(),
  method: z.string(),
});

const SellerProtectionSchema = z.object({
  status: z.string(),
  dispute_categories: z.array(z.string()),
});

const SellerReceivableBreakdownSchema = z.object({
  gross_amount: AmountSchema,
  paypal_fee: AmountSchema,
  net_amount: AmountSchema,
});

const CaptureSchema = z.object({
  id: z.string(),
  status: z.string(),
  amount: AmountSchema,
  seller_protection: SellerProtectionSchema,
  final_capture: z.boolean(),
  disbursement_mode: z.string(),
  seller_receivable_breakdown: SellerReceivableBreakdownSchema,
  create_time: z.string().datetime(),
  update_time: z.string().datetime(),
  links: z.array(LinkSchema),
});

const AuthorizationSchema = z.object({
  status: z.string(),
  status_details: z.object({
    reason: z.string(),
  }),
  id: z.string(),
  invoice_id: z.string(),
  custom_id: z.string(),
  links: z.array(
    z.object({
      href: z.string().nullable(),
      rel: z.string().nullable(),
      method: z.string().nullable(),
    }),
  ),
  amount: AmountSchema,
  network_transaction_reference: z.object({
    id: z.string(),
    date: z.string(),
    acquirer_reference_number: z.string(),
    network: z.string(),
  }),
  seller_protection: z.object({
    status: z.string(),
    dispute_categories: z.array(z.string().nullable()),
  }),
  expiration_time: z.string().datetime(),
  create_time: z.string().datetime(),
  update_time: z.string().datetime(),
  processor_response: z.object({
    avs_code: z.string(),
    cvv_code: z.string(),
    response_code: z.string(),
    payment_advice_code: z.string(),
  }),
});

const PurchaseUnitSchema = z.object({
  reference_id: z.string(),
  shipping: z.object({
    address: z.object({
      address_line_1: z.string(),
      address_line_2: z.string(),
      admin_area_2: z.string(),
      admin_area_1: z.string(),
      postal_code: z.string(),
      country_code: z.string(),
    }),
  }),
  payments: z.object({
    captures: z.array(CaptureSchema),
    authorizations: z.array(AuthorizationSchema),
  }),
});

export const CapturePaypalAPIResponseSchema = z.object({
  id: z.string(),
  status: z.number(),
  details: z.array(PaypalErrorSchema).optional(),
  debug_id: z.string().optional(),
  purchase_units: z.array(PurchaseUnitSchema),
});

// You can also infer the TypeScript types directly from the schemas
export type CreatePaypalAPIResponse = z.infer<typeof CreatePaypalAPIResponseSchema>;
export type CapturePaypalAPIResponse = z.infer<typeof CapturePaypalAPIResponseSchema>;

export const CreateStripeCheckoutSessionRequestSchema = z.object({
  userId: z.uuid(),
  transactionIds: z.array(z.number()),
});

export type CreateStripeCheckoutSessionRequest = z.infer<
  typeof CreateStripeCheckoutSessionRequestSchema
>;

export const CreateStripeCheckoutSessionResponseSchema = z.object({
  sessionId: z.string(),
  url: z.string().url(),
});

export type CreateStripeCheckoutSessionResponse = z.infer<
  typeof CreateStripeCheckoutSessionResponseSchema
>;

export type CreateStripeResponse = ProblemJson | CreateStripeCheckoutSessionResponse;

export const AddTutorToClassRequestSchema = z.object({
  sessionId: z.number(),
  tutorId: z.uuid(),
});
export type AddTutorToClassRequest = z.infer<typeof AddTutorToClassRequestSchema>;

export const RemoveTutorFromClassRequestSchema = z.object({
  sessionId: z.number(),
  tutorId: z.uuid(),
});
export type RemoveTutorFromClassRequest = z.infer<typeof RemoveTutorFromClassRequestSchema>;
